import React, { useCallback, useEffect, useState } from "react";

import moment from "moment";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import Select from "react-select";
import { useMount } from "react-use";

import useUrlTab from "@/src/hooks/useUrlTab";

import UserActivityTab from "./UserActivityTab";
import UserCustomInput from "./UserCustomInput";
import "../../../css/admin/users.scss";
import "../../../css/admin/utils/item-status.scss";
import { allowedLng } from "../../../i18n";
import { getLanguageUuid } from "../../../utils/data/Content";
import {
  getCountryByCode,
  getCountryOptions,
} from "../../../utils/data/Countries";
import CappedPage from "../../common/CappedPage";
import NavTabs from "../../common/NavTabs";
import ProfileIcons from "../../common/profile/ProfileIcons";
import NotifMessage from "../../common/tools/NotifMessage";
import ConfirmModal from "../../modal/ConfirmModal";
import Breadcrumbs from "../../utils/Breadcrumbs";
import ContainerHeader from "../../utils/ContainerHeader";
import SimpleImageUpload from "../../utils/SimpleImageUpload";

import type { JSX } from "react";

import type { ValidUser } from "./UsersAdminCreate";
import type { GetUserProfileResponse } from "../../../api/AdminUserAPI.types";
import type { AppState } from "../../../reducers";
import type { NavTabItem } from "../../common/NavTabs";
import type { ModalActions } from "../../modal/Modal";

const TAB_PROFILE = "profile";
const TAB_ACTIVITY = "activity";
const AVAILABLE_TAB_LIST = [TAB_PROFILE, TAB_ACTIVITY];

export interface Notification {
  registration?: boolean;
  create_password?: boolean;
}

interface UsersAdminFormProps {
  commentsDisabled: boolean;
  isDisabled: boolean;
  type: string;
  item: Omit<GetUserProfileResponse, "notification_settings">;
  notifications?: Notification;
  valid: ValidUser;
  onSubmit: (user: UpdatedUser, notifications?: Notification) => void;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const validInputNames = {
  country: "country",
  country_uuid: "country_uuid",
  language: "language",
  language_uuid: "language_uuid",
  first_name: "first_name",
  last_name: "last_name",
  email: "email",
  password: "password",
  internal_comment: "internal_comment",
};

export interface UpdatedUser extends Omit<
  GetUserProfileResponse,
  "notification_settings"
> {
  avatar?: string | ArrayBuffer | null;
  password?: string;
  language_uuid?: string;
  block_comments?: boolean;
}

type ValidInputNamesTypes = keyof typeof validInputNames;

type NotificationTypes = keyof Notification;

type BlockAccountActions = "block" | "unblock" | null;

interface CountryOption {
  value: string;
  label: string;
  code: string;
}

interface LanguageOption {
  value: string;
  label: string;
  uuid?: string;
}

export default function UsersAdminForm({
  item,
  notifications: notificationOptions,
  onSubmit,
  type,
  valid,
  commentsDisabled,
  isDisabled,
}: UsersAdminFormProps): JSX.Element {
  const { t } = useTranslation("editor-users");
  const [user, setUser] = useState<UpdatedUser>(item);
  const [action, setAction] = useState<BlockAccountActions>(null);
  const [country, setCountry] = useState<CountryOption | null>(null);
  const [language, setLanguage] = useState<LanguageOption | null>(null);
  const [notifications, setNotifications] = useState<Notification | undefined>(
    notificationOptions,
  );
  const countries = useSelector(
    (state: AppState) => state.fetchReducer.countries,
  );
  const languages = useSelector(
    (state: AppState) => state.fetchReducer.languages,
  );
  const storefrontProjectParams = useSelector(
    (state: AppState) =>
      state.architectureReducer.project?.storefront_project_params,
  );

  const { activeTab, setActiveTab } = useUrlTab({
    availableTabs: AVAILABLE_TAB_LIST,
    defaultTab: TAB_PROFILE,
  });

  useEffect(() => {
    if (language?.value && language.value !== item.lang) {
      setUser({
        ...user,
        custom_1: null,
        custom_2: null,
        custom_3: null,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language]);

  const getLanguage = useCallback(() => {
    if (storefrontProjectParams?.display_user_language && item.lang) {
      const lang = getLanguageUuid(languages, item.lang);

      if (lang)
        setLanguage({
          label: t(`languages:langs.${item.lang}`),
          value: item.lang,
          uuid: lang.language_uuid,
        });
    }
  }, [storefrontProjectParams, t, languages, item.lang]);

  const getCountry = useCallback(() => {
    if (
      storefrontProjectParams?.display_user_country &&
      item.country &&
      countries?.data?.length
    ) {
      const newCountry = getCountryByCode(
        countries,
        item.country,
        t("languages:countries", { returnObjects: true }),
      );

      if (newCountry) setCountry(newCountry);
    }
  }, [
    storefrontProjectParams?.display_user_country,
    t,
    countries,
    item.country,
  ]);

  useMount(() => {
    setUser(item);
    getLanguage();
    getCountry();
  });

  const onGetCountryOptions = (): CountryOption[] =>
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    getCountryOptions(countries, false);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    e.preventDefault();
    const reader = new FileReader();
    const file = e.target.files?.[0];

    if (!file) return;

    reader.onloadend = (): void => setUser({ ...user, avatar: reader.result });
    reader.readAsDataURL(file);
  };

  const handleInputChange = useCallback(
    (
      param: ValidInputNamesTypes,
      target:
        | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>["target"]
        | {
            value: string;
          },
    ): void => {
      const newUser = {
        ...user,
        [param]: target.value,
      };

      setUser(newUser);
    },
    [user],
  );

  const handleCheckboxChange = useCallback(
    (param: NotificationTypes, value: boolean): void => {
      const newNotif = {
        ...notifications,
        [param]: value,
      };

      setNotifications(newNotif);
    },
    [notifications],
  );

  const handleChange = useCallback(
    (param: ValidInputNamesTypes, value: CountryOption | LanguageOption) => {
      switch (param) {
        case "country_uuid":
          setCountry(value as CountryOption);
          handleInputChange("country_uuid", value);
          break;
        case "language":
          {
            setLanguage(value as LanguageOption);
            const languageValue = languages.data.find(
              (lang) => value.value === lang.language,
            )?.uuid;
            const languageId = {
              value: languageValue ?? "",
            };
            handleInputChange("language_uuid", languageId);
          }
          break;
        default:
          break;
      }
    },
    [languages.data, handleInputChange],
  );

  const actionOnBlock = (updatedAction: BlockAccountActions): void => {
    setAction(updatedAction);
  };

  const confirmActionOnBlock = (): void => {
    setAction(null);

    const blockComments = action === "block";

    onSubmit({ ...user, block_comments: blockComments }, notifications);
  };

  const save = (): void => {
    onSubmit(user, notifications);
  };

  const {
    display_user_country: displayUserCountry,
    display_user_language: displayUserLanguage,
  } = storefrontProjectParams ?? {};

  const allowedLanguageList = allowedLng.map((lang) => ({
    label: t(`languages:langs.${lang}`),
    value: lang,
  }));
  const countryOptions = onGetCountryOptions();
  const isNew = type === "new";

  const breadcrumbs = [
    {
      name: t("editor-menu:analytics"),
      url: "/users",
    },
    {
      name: t("userTitle"),
      url: "/users",
    },
    {
      name: t(type),
    },
  ];

  const modalActions: ModalActions[] = [
    {
      label: t("modal:cancel"),
      onClick: (): void => actionOnBlock(null),
      buttonVariant: "secondary",
    },
    {
      label: t("modal:confirm"),
      onClick: confirmActionOnBlock,
      buttonVariant: "delete",
    },
  ];

  const userCreationDate = user.inserted_at ? moment(user.inserted_at) : null;

  const profileForm = (
    <div className="editor-tile-container m-0!">
      <div className="flex-hor flex-align-center m-t-20 flex-justify-center okast-flex-1 flex-wrap">
        <div className="editor-user-edit-form">
          <div className="editor-form-section flex-align-center m-v-30 okast-flex-1">
            <div className="editor-form-section-title logo">{t("logo")}</div>
            <SimpleImageUpload
              rounded
              format="1_1"
              id="user"
              isDisabled={isDisabled}
              onImageChange={handleImageChange}
              value={user.avatar ?? user.avatar_url}
            />
            <ProfileIcons
              actionOnBlock={actionOnBlock}
              canBlockUser={!isDisabled}
              showComments={!commentsDisabled}
              user={user}
            />
          </div>
          <div className="user-info-form">
            <div className="user-info-form__wrapper">
              <div className="editor-form-section">
                <div className="editor-form-section-title">
                  {t("first_name")}
                </div>
                <div className="editor-form-input-box-h full-w">
                  <input
                    className="editor-form-input-title"
                    disabled={isDisabled}
                    type="text"
                    value={user.first_name || ""}
                    onChange={(e): void => {
                      handleInputChange("first_name", e.target);
                    }}
                  />
                </div>
              </div>
              <div className="editor-form-section">
                <div className="editor-form-section-title">
                  {t("last_name")}
                </div>
                <div className="editor-form-input-box-h full-w">
                  <input
                    className="editor-form-input-title"
                    disabled={isDisabled}
                    type="text"
                    value={user.last_name || ""}
                    onChange={(e): void => {
                      handleInputChange("last_name", e.target);
                    }}
                  />
                </div>
              </div>
            </div>
            {isNew ? (
              <div className="user-info-form__wrapper">
                <div className="editor-form-section">
                  <div className="editor-form-section-title">{t("email")}</div>
                  <div className="editor-form-input-box-h full-w">
                    <input
                      className="editor-form-input-title"
                      disabled={isDisabled}
                      type="text"
                      value={user.email || ""}
                      onChange={(e): void => {
                        handleInputChange("email", e.target);
                      }}
                    />
                  </div>
                </div>
                <div className="editor-form-section">
                  <div className="editor-form-section-title">
                    {t("setPassword")}
                  </div>
                  <div className="editor-form-input-box-h full-w">
                    <input
                      className="editor-form-input-title"
                      disabled={isDisabled}
                      type="password"
                      value={user.password ?? ""}
                      onChange={(e): void => {
                        handleInputChange("password", e.target);
                      }}
                    />
                  </div>
                </div>
              </div>
            ) : (
              <React.Fragment>
                <div className="user-info-form__wrapper">
                  <div className="editor-form-section">
                    <div className="editor-form-section-title">
                      {t("email")}
                    </div>
                    <div className="editor-form-input-box-h full-w">
                      <input
                        className="editor-form-input-title"
                        disabled={isDisabled}
                        type="text"
                        value={user.email || ""}
                        onChange={(e): void => {
                          handleInputChange("email", e.target);
                        }}
                      />
                    </div>
                  </div>
                  <div className="editor-form-section">
                    <div className="editor-form-section-title">
                      {t("created_date")}
                    </div>
                    <div className="editor-form-input-box-h full-w">
                      <input
                        disabled
                        className="editor-form-input-title"
                        type="text"
                        value={moment(user.inserted_at).format(
                          "DD/MM/YYYY HH:mm",
                        )}
                      />
                    </div>
                  </div>
                </div>
                <div className="user-info-form__wrapper">
                  <div className="editor-form-section">
                    <div className="editor-form-section-title">
                      {t("password")}
                    </div>
                    <div className="editor-form-input-box-h full-w">
                      <input
                        className="editor-form-input-title"
                        disabled={isDisabled}
                        type="password"
                        value={user.password ?? ""}
                        onChange={(e): void => {
                          handleInputChange("password", e.target);
                        }}
                      />
                    </div>
                  </div>
                  <div className="editor-form-section" />
                </div>
              </React.Fragment>
            )}
            <div className="user-info-form__wrapper">
              {[1 as const, 2 as const, 3 as const].map((customNumber) => {
                if (
                  storefrontProjectParams?.[
                    `display_user_custom_${customNumber}`
                  ]
                ) {
                  return (
                    <UserCustomInput
                      key={`user-custom-input-${customNumber}`}
                      id={`custom_${customNumber}`}
                      isDisabled={isDisabled}
                      onChange={handleInputChange}
                      user_lang={language}
                      value={user[`custom_${customNumber}`] ?? ""}
                    />
                  );
                }
                return null;
              })}
            </div>
            <div className="user-info-form__wrapper">
              {displayUserCountry ? (
                <div className="editor-form-section">
                  <div className="editor-form-section-title">
                    {t("common:country")}
                  </div>
                  <div className="editor-form-input-box-h full-w">
                    <Select
                      className="editor-select editor-user-select-country"
                      classNamePrefix="slct"
                      isDisabled={isDisabled}
                      name="editor-form-select-country"
                      options={countryOptions}
                      value={country}
                      onChange={(
                        updatedCountry: CountryOption | null,
                      ): void => {
                        if (!updatedCountry) return;
                        handleChange("country_uuid", updatedCountry);
                      }}
                    />
                  </div>
                </div>
              ) : null}
              {displayUserLanguage ? (
                <div className="editor-form-section">
                  <div className="editor-form-section-title">
                    {t("common:language")}
                  </div>
                  <div className="editor-form-input-box-h full-w">
                    <Select
                      className="editor-select editor-user-select-language"
                      classNamePrefix="slct"
                      isDisabled={isDisabled}
                      name="editor-form-select-language"
                      options={allowedLanguageList}
                      value={language}
                      onChange={(
                        updatedLanguage: LanguageOption | null,
                      ): void => {
                        if (!updatedLanguage) return;
                        handleChange("language", updatedLanguage);
                      }}
                    />
                  </div>
                </div>
              ) : null}
            </div>
            <div className="editor-form-section">
              <div className="editor-form-section-title">
                {t("editor-users:internalComment")}
              </div>
              <textarea
                className="border-input mr-5 rounded border p-2.5"
                value={user.internal_comment ?? ""}
                onChange={(event): void => {
                  handleInputChange("internal_comment", event.target);
                }}
              />
            </div>
            {isNew ? (
              <div className="editor-form-section">
                <div className="editor-form-section-title notifications">
                  {t("notifications")}
                  <div className="editor-tooltip editor-tooltip-center">
                    <i className="material-icons">info</i>
                    <span className="editor-tooltiptext">
                      {t(`infoNotifications`)}
                    </span>
                  </div>
                </div>
                <div className="editor-form-input">
                  <div className="editor-form-inline editor-form-notifications">
                    {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                    <label className="checkbox">
                      <span>{t("registration")}</span>
                      <input
                        checked={notifications?.registration}
                        className="checkbox-input"
                        id="notif_registration"
                        name="notif_registration"
                        type="checkbox"
                        onChange={(e): void => {
                          handleCheckboxChange(
                            "registration",
                            e.target.checked,
                          );
                        }}
                      />
                      <span className="checkmark"> </span>
                    </label>
                  </div>
                  <div className="editor-form-inline editor-form-notifications">
                    {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                    <label className="checkbox">
                      <span>{t("create_password")}</span>
                      <input
                        checked={notifications?.create_password}
                        className="checkbox-input"
                        id="notif_create_password"
                        name="notif_create_password"
                        type="checkbox"
                        onChange={(e): void => {
                          handleCheckboxChange(
                            "create_password",
                            e.target.checked,
                          );
                        }}
                      />
                      <span className="checkmark"> </span>
                    </label>
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );

  const isFormTab = isNew || activeTab === TAB_PROFILE;

  const tabList: readonly NavTabItem[] = [
    {
      value: TAB_PROFILE,
      label: t("editor-analytics:tabs.profile"),
      content: profileForm,
    },
    {
      value: TAB_ACTIVITY,
      label: t("editor-analytics:tabs.activity"),
      content: (
        <UserActivityTab
          userCreationDate={userCreationDate}
          userUuid={user.uuid}
        />
      ),
    },
  ];

  return (
    <CappedPage contentClassName="editor-user-form-container pt-[30px] pb-15">
      <Breadcrumbs items={breadcrumbs} />
      <ContainerHeader
        withBackBtn
        canSave={!isDisabled}
        onSave={isFormTab ? save : undefined}
        type="user"
        item={{
          ...user,
          label: isNew
            ? t("userNew")
            : t("usersProfile", {
                name: `${user.first_name} ${user.last_name}`,
              }),
        }}
      />
      {valid?.error && !valid?.isValid ? (
        <NotifMessage message={t(`error:${valid.error}`)} severity="error" />
      ) : null}
      {isNew ? (
        profileForm
      ) : (
        <NavTabs
          onValueChange={setActiveTab}
          tabList={tabList}
          value={activeTab}
        />
      )}
      <ConfirmModal
        actions={modalActions}
        isOpen={!!action}
        onClose={(): void => actionOnBlock(null)}
        size="md"
        title={t("popup:confirm")}
      >
        {t(`popup:confirm_${action}`, { user: user.email })}
      </ConfirmModal>
    </CappedPage>
  );
}
